import { ActionIcon, Combobox, NumberInput, Select, TextInput, useCombobox } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import classNames from 'classnames/bind';
import { i18nKeys, useTranslation } from 'locales';
import { type LightProduct, useLoadProducts } from 'modules/Products/api';
import { useMemo, useState } from 'react';
import Amount from 'shared/components/Amount';
import { IconName } from 'shared/components/Icon';
import Percentage from 'shared/components/Percentage';
import Tooltip from 'shared/components/Tooltip';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';

import { useInvoiceFormContext } from '../../InvoiceForm.context';
import type { InvoiceFormSharedProps } from '../../InvoiceForm.types';
import { applyCreditNoteSign } from '../../InvoiceForm.utils';

import styleIdentifiers from './InvoiceProductLine.module.scss';

const styles = classNames.bind(styleIdentifiers);

type InvoiceProductLineProps = InvoiceFormSharedProps & {
  lineIndex: number;
  removeProductLine: (index: number) => void;
};

export default function InvoiceProductLine({
  lineIndex,
  removeProductLine,
  isCreditNote,
  noTva,
  currency,
  constants,
}: InvoiceProductLineProps) {
  const { t } = useTranslation();
  const form = useInvoiceFormContext();

  const [productSearchName, setProductSearchName] = useState<string>('');
  const { products } = useLoadProducts({ name: productSearchName || undefined });
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const product_lines_attributes = form.values.product_lines_attributes;
  const debtor = form.values.debtor_attributes;
  const formatter = useLocalizedCurrencyFormatter(false, currency);

  const productLine = product_lines_attributes?.[lineIndex];
  const product = productLine?.product_attributes;

  const onSelectProduct = ({ item }: { item: LightProduct }) => {
    const price = applyCreditNoteSign(isCreditNote, item.priceHtva);

    form.setFieldValue(`product_lines_attributes.${lineIndex}`, {
      ...productLine,
      id: undefined,
      product_id: item.id,
      quantity: 1,
      isInvoice: false,
      product_attributes: {
        tax_category_code: noTva ? '00' : item.taxCategoryCode,
        current_unity_price_htva: price,
        name: item.name,
      },
    });
  };

  const onChangeInputProduct = () => {
    if (productLine?.product_id) {
      form.setFieldValue(`product_lines_attributes.${lineIndex}.product_id`, undefined);
      form.setFieldValue(`product_lines_attributes.${lineIndex}.id`, undefined);
    }
  };

  const tvaCode = product?.tax_category_code;
  const tvaObj = tvaCode ? constants.tax_categories.find(it => it.value === tvaCode) : undefined;
  const tvaRate = noTva ? 0 : Number(tvaObj?.vat_value) || 0;

  const taxCategoryData = useMemo(() => {
    const seen = new Set<string>();
    return constants.tax_categories.reduce<Array<{ label: string; value: string }>>(
      (acc, tc: { description: string; value: string }) => {
        if (!seen.has(tc.value)) {
          seen.add(tc.value);
          acc.push({ label: tc.description, value: tc.value });
        }
        return acc;
      },
      [],
    );
  }, [constants.tax_categories]);

  const quantity = Number(productLine?.quantity ?? 0);
  const unitPrice = Number(product?.current_unity_price_htva ?? 0);
  const discountRate = Number(productLine?.discount_rate ?? 0);

  // Totals are pure derivations of quantity/unit price/discount/VAT — compute
  // them at render rather than mirroring them into form state. The serializer
  // recomputes them at submit (`invoice.serializer.ts`), so no consumer relies
  // on form-state totals being authoritative for non-invoice lines.
  const baseAmount = quantity * unitPrice * (1 - discountRate / 100);
  const totalHtva = roundHalfUp(baseAmount, 2);
  const totalTvac = roundHalfUp(baseAmount * (1 + tvaRate / 100), 2);

  const removeProduct = () => {
    removeProductLine(lineIndex);
  };
  const productOptions = (products?.data ?? []).map((item: LightProduct) => (
    <Combobox.Option value={item.id} key={item.id}>
      <div className={styles('product-item')}>
        <span>{item.name}</span>
        <span>
          {t(i18nKeys.EXCL_VAT_PRICE)}: {formatter.format(item.priceHtva)}
        </span>
      </div>
    </Combobox.Option>
  ));

  const nameProps = form.getInputProps(
    `product_lines_attributes.${lineIndex}.product_attributes.name`,
  );
  const priceInputProps = form.getInputProps(
    `product_lines_attributes.${lineIndex}.product_attributes.current_unity_price_htva`,
  );

  const handlePriceChange = (next: number | string) =>
    priceInputProps.onChange(applyCreditNoteSign(isCreditNote, next));

  return (
    <tr className={styles('invoice-product-line')}>
      <td>
        <Combobox
          store={combobox}
          onOptionSubmit={selectedId => {
            const selectedProduct = (products?.data ?? []).find(
              (p: LightProduct) => p.id === selectedId,
            );
            if (selectedProduct) onSelectProduct({ item: selectedProduct });
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <TextInput
              value={nameProps.value ?? ''}
              onChange={event => {
                const newValue = event.currentTarget.value;
                nameProps.onChange(newValue);
                setProductSearchName(newValue);
                onChangeInputProduct();
                combobox.openDropdown();
              }}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
              placeholder={t(i18nKeys.INVOICING.CREATE.CHOOSE_PRODUCT)}
              className={styles('invoice-select-input-wrapper')}
              size="xs"
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              {productOptions.length > 0 ? (
                productOptions
              ) : (
                <Combobox.Empty>{t(i18nKeys.NO_RESULT)}</Combobox.Empty>
              )}
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>
      </td>
      <td>
        <NumberInput
          {...form.getInputProps(`product_lines_attributes.${lineIndex}.quantity`)}
          className={styles('invoice-input')}
          size="xs"
          hideControls
          placeholder="0"
          min={0}
          step={0.01}
          decimalScale={2}
        />
      </td>
      <td>
        <NumberInput
          {...priceInputProps}
          onChange={handlePriceChange}
          className={styles('invoice-input')}
          size="xs"
          hideControls
          placeholder="0.00"
          step={0.0001}
          decimalScale={4}
        />
      </td>
      <td>
        <NumberInput
          {...form.getInputProps(`product_lines_attributes.${lineIndex}.discount_rate`)}
          className={styles('invoice-input')}
          size="xs"
          hideControls
          placeholder="0%"
          min={0}
          max={100}
          step={0.01}
          decimalScale={2}
        />
      </td>
      <td className={styles('amount')}>
        <div className={styles('htva-price')}>
          <Amount value={totalHtva} suffix={currency} />
        </div>
      </td>
      <td>
        {(productLine?.product_id || noTva) && (
          <div className={styles('amount')}>
            <Percentage value={tvaRate} />
            {noTva && (
              <Tooltip icon={IconName.INFO}>
                {t(
                  debtor?.co_contractor
                    ? i18nKeys.FORM.INVOICING.VAT_COCONTRACTOR
                    : i18nKeys.FORM.INVOICING.VAT_INTRACOM,
                )}
              </Tooltip>
            )}
          </div>
        )}
        <div className={productLine?.product_id || noTva ? 'invisible-input' : ''}>
          <Select
            {...form.getInputProps(
              `product_lines_attributes.${lineIndex}.product_attributes.tax_category_code`,
            )}
            data={taxCategoryData}
            searchable
            className={styles('tva', 'invoice-select')}
            size="xs"
            placeholder={t(i18nKeys.SELECT)}
          />
          {noTva && (
            <div className={styles('special-tva-info')}>
              {t(i18nKeys.INVOICING.CREATE.VAT_APPLIED_0)}
            </div>
          )}
        </div>
      </td>
      <td className={styles('amount')}>
        <div className={styles('htva-price')}>
          <Amount value={totalTvac} suffix={currency} />
        </div>
      </td>
      <td>
        <ActionIcon variant="subtle" color="red" onClick={removeProduct}>
          <IconTrash size={16} stroke={1.5} />
        </ActionIcon>
      </td>
    </tr>
  );
}

// Round half up, using toPrecision(12) to strip accumulated floating-point noise
const roundHalfUp = (value: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round(+(value * factor).toPrecision(12)) / factor;
};
